import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, HostListener, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { takeUntil, switchMap, filter } from 'rxjs/operators';
import { LiveStreamService } from 'src/app/_service/live-stream.service';
import { LiveStream } from 'src/app/_model/live-stream.model';
import { WhiteLabelService } from 'src/app/_service/white-label.service';
import { IosBridgeService } from 'src/app/_service/ios-bridge.service';
import { URLS } from 'src/app/constants/URLS';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-live-stream-section',
  templateUrl: './live-stream-section.component.html',
  styleUrls: ['./live-stream-section.component.css']
})
export class LiveStreamSectionComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() showHeader: boolean = true;

  @ViewChild('carouselTrack') carouselTrack: ElementRef;
  @ViewChild('owlCarousel') carouselContainer: ElementRef;

  streams: LiveStream[] = [];
  isLoading: boolean = true;

  // Carousel state
  currentIndex: number = 0;
  translateX: number = 0;
  isAnimating: boolean = false;
  itemWidth: number = 360;
  marginRight: number = 16;
  maxIndex: number = 0;
  showNavigation: boolean = false;
  itemsPerView: number = 4;

  // Responsive config (breakpoint -> items/margin/nav). Mirrors spread-carousel's
  // carouselOptions.responsive EXACTLY so live cards line up 1:1 with the
  // continue-watching / other rows in category-movie at every breakpoint.
  private readonly responsive: { [minWidth: number]: { items: number; margin: number; nav: boolean } } = {
    0:    { items: 2,    margin: 3, nav: false },
    400:  { items: 2.05, margin: 6, nav: false },
    600:  { items: 3,    margin: 3, nav: false },
    800:  { items: 5,    margin: 8, nav: true  },
    1000: { items: 5,    margin: 8, nav: true  },
    1200: { items: 5,    margin: 8, nav: true  },
    1600: { items: 6,    margin: 8, nav: true  },
  };

  // Skeleton placeholders — count + width match the responsive config so loading
  // state mirrors what's about to render
  skeletonItems: number[] = [];

  // Drag state
  isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragCurrentX: number = 0;
  private dragStartTranslate: number = 0;

  // Countdown timers — store precomputed strings
  countdownMap: { [streamId: number]: string } = {};
  private countdownInterval: any;

  private destroy$ = new Subject<void>();

  constructor(
    private liveStreamService: LiveStreamService,
    private router: Router,
    private wl: WhiteLabelService,
    private iosBridge: IosBridgeService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateSkeletonForCurrentBreakpoint();
    this.loadStreams();
    this.startPolling();
    this.startCountdownTimer();
  }

  ngAfterViewInit(): void {
    // Size the skeleton cards immediately so loading state matches final layout
    this.calculateDimensions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateSkeletonForCurrentBreakpoint();
    this.calculateDimensions();
  }

  /** Resolves the responsive config for the current viewport width. */
  private getCurrentResponsiveConfig(): { items: number; margin: number; nav: boolean } {
    const screenWidth = window.innerWidth;
    const breakpoints = Object.keys(this.responsive).map(k => parseInt(k, 10)).sort((a, b) => b - a);
    for (const bp of breakpoints) {
      if (screenWidth >= bp) return this.responsive[bp];
    }
    return this.responsive[0];
  }

  /** Rebuilds the skeleton placeholder list to match the current breakpoint's items count. */
  private updateSkeletonForCurrentBreakpoint(): void {
    const config = this.getCurrentResponsiveConfig();
    const count = Math.ceil(config.items) + 1; // +1 so the peek slot also shimmers
    this.skeletonItems = Array.from({ length: count }, (_, i) => i);
    this.marginRight = config.margin;
  }

  private loadStreams(): void {
    this.liveStreamService.getDiscoveryStreams()
      .pipe(takeUntil(this.destroy$))
      .subscribe(streams => {
        this.streams = this.sortStreams(streams);
        this.isLoading = false;
        this.updateCountdowns();
        setTimeout(() => this.calculateDimensions(), 0);
        this.cdr.detectChanges();
      });
  }

  private startPolling(): void {
    // Refresh stream list every 60 seconds, pause when tab is hidden
    interval(60000).pipe(
      takeUntil(this.destroy$),
      filter(() => !document.hidden),
      switchMap(() => this.liveStreamService.getDiscoveryStreams())
    ).subscribe(streams => {
      this.streams = this.sortStreams(streams);
      this.updateCountdowns();
      this.cdr.detectChanges();
    });
  }

  /** LIVE streams first (sorted by viewer count desc), then SCHEDULED by start time */
  private sortStreams(streams: LiveStream[]): LiveStream[] {
    const live = streams
      .filter(s => s.status === 'LIVE' || s.status === 'RECONNECTING')
      .sort((a, b) => (b.viewerCount || 0) - (a.viewerCount || 0));
    const scheduled = streams
      .filter(s => s.status === 'SCHEDULED' || s.status === 'PROVISIONED')
      .sort((a, b) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime());
    return [...live, ...scheduled];
  }

  // --- Countdown logic ---

  private startCountdownTimer(): void {
    this.countdownInterval = setInterval(() => {
      this.updateCountdowns();
      this.cdr.detectChanges();
    }, 1000);
  }

  private updateCountdowns(): void {
    const now = Date.now();
    for (const stream of this.streams) {
      if (stream.status === 'SCHEDULED' || stream.status === 'PROVISIONED') {
        const diff = new Date(stream.scheduledStart).getTime() - now;
        this.countdownMap[stream.id] = diff > 0 ? this.formatCountdown(diff) : 'Starting soon';
      }
    }
  }

  private formatCountdown(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  // --- Card helpers ---

  getStreamThumbnail(stream: LiveStream): string {
    if (!stream.thumbnailUrl) return '';
    if (/^https?:\/\//i.test(stream.thumbnailUrl)) return stream.thumbnailUrl;
    const base = environment.baseUrl.replace(/\/$/, '');
    const path = stream.thumbnailUrl.startsWith('/') ? stream.thumbnailUrl : `/${stream.thumbnailUrl}`;
    return `${base}${path}`;
  }

  formatViewerCount(count: number): string {
    if (!count) return '0';
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count.toString();
  }

  isLive(stream: LiveStream): boolean {
    return stream.status === 'LIVE' || stream.status === 'RECONNECTING';
  }

  isScheduled(stream: LiveStream): boolean {
    return stream.status === 'SCHEDULED' || stream.status === 'PROVISIONED';
  }

  getAccessLabel(stream: LiveStream): string {
    if (stream.accessType === 'SUBSCRIPTION') return 'SUB';
    if (stream.accessType === 'PPV') return stream.ppvPrice ? `${stream.ppvPrice}` : 'PPV';
    return '';
  }

  onStreamClick(stream: LiveStream): void {
    const route = this.wl.getPrefixedRoute('live', 'event', String(stream.id));
    // Native players take over LIVE playback via the video bridge (same path as
    // VOD): iOS -> iosVideoPlayer handler, Android TV -> AndroidTV.openVideoPlayer.
    // Scheduled/non-live cards still navigate to the web route, which shows the
    // countdown/waiting overlay inside the webview.
    const win = window as any;
    const isAndroidTv = !!win?.AndroidTV && typeof win.AndroidTV.openVideoPlayer === 'function';
    if (this.isLive(stream) && (this.iosBridge.isIOS() || isAndroidTv)) {
      this.openLiveOnNative(stream, route);
      return;
    }
    this.router.navigate([route]);
  }

  /**
   * Hands a live stream to the native player via the existing video bridge
   * (the same channel VOD uses — the native side branches on `isLive: true`).
   *
   * Unlike VOD — where native derives the stream URL from `vidLibId` — a live
   * stream's URL only exists after getPlaybackUrl(), so we fetch it here and pass
   * the stream URL + DRM explicitly. Each platform keeps its existing VOD payload
   * dialect so no native handler needs reshaping:
   *   - iOS: `video` field, posted to the iosVideoPlayer handler; plays HLS (FairPlay).
   *   - Android TV: `videoLibrary` field, sent directly to AndroidTV.openVideoPlayer
   *     as a JSON string; ExoPlayer prefers DASH (Widevine), falls back to HLS.
   *
   * We do NOT navigate: returnRoute stays on the discovery page so the native
   * "minimize/back" returns the user here, exactly like the VOD flow.
   */
  private openLiveOnNative(stream: LiveStream, route: string): void {
    this.liveStreamService.getPlaybackUrl(stream.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        const data = response?.data ?? response;
        const win = window as any;
        const isAndroidTv = !!win?.AndroidTV && typeof win.AndroidTV.openVideoPlayer === 'function';

        // Shared metadata bean (the VOD `video` / `videoLibrary` equivalent).
        const library = {
          vidLibId: stream.id,
          videoTitle: stream.title,
          title: stream.title,
          description: stream.description,
          channelId: stream.channelId,
          channelName: stream.channelName,
          channelImageUrl: stream.channelImageUrl,
          thumbnailUrl: stream.thumbnailUrl,
          accessType: stream.accessType,
          isLive: true,
        };
        const playerUrl = `${this.iosBridge.getCurrentDomain()}${route}`;

        if (isAndroidTv) {
          // ExoPlayer: prefer DASH (Widevine), fall back to HLS.
          const videoUrl = data?.dashUrl || data?.hlsUrl;
          // No URL yet (backend status can lag actual broadcast) — fall back to
          // the web route so the user sees the waiting overlay, not a dead tap.
          if (!videoUrl) {
            this.router.navigate([route]);
            return;
          }
          const headers = data?.drmHeaders || {};
          const payload = {
            isLive: true,
            streamId: stream.id,
            videoUrl,
            hlsUrl: data?.hlsUrl || '',
            dashUrl: data?.dashUrl || '',
            drmLicenseUrl: data?.licenseUrl || '',
            authToken: headers['X-AxDRM-Message'] || headers['x-axdrm-message'] || '',
            drmHeaders: headers,
            videoLibrary: library,
            returnRoute: this.router.url,
            playerUrl,
          };
          win.AndroidTV.openVideoPlayer(JSON.stringify(payload));
          return;
        }

        // iOS: plays HLS with FairPlay.
        const hlsUrl = data?.hlsUrl;
        if (!hlsUrl) {
          this.router.navigate([route]);
          return;
        }
        const payload = {
          isLive: true,
          streamId: stream.id,
          videoUrl: hlsUrl,
          hlsUrl: hlsUrl,
          dashUrl: data?.dashUrl || '',
          drmLicenseUrl: data?.licenseUrl || '',
          drmHeaders: data?.drmHeaders || {},
          video: library,
          returnRoute: this.router.url,
          playerUrl,
        };
        this.iosBridge.openVideoPlayer(payload);
      });
  }

  getStatusLabel(stream: LiveStream): string {
    if (this.isLive(stream)) return 'LIVE';
    const diff = new Date(stream.scheduledStart).getTime() - Date.now();
    if (diff <= 0) return 'STARTING SOON';
    if (diff < 60 * 60 * 1000) return 'STARTING SOON';
    return 'UPCOMING';
  }

  trackByStreamId(index: number, stream: LiveStream): number {
    return stream.id;
  }

  // --- Carousel navigation ---

  private calculateDimensions(): void {
    if (!this.carouselContainer?.nativeElement) return;
    const containerWidth = this.carouselContainer.nativeElement.offsetWidth;
    if (containerWidth === 0) {
      setTimeout(() => this.calculateDimensions(), 50);
      return;
    }

    const config = this.getCurrentResponsiveConfig();
    this.itemsPerView = config.items;
    this.marginRight = config.margin;

    // Compute itemWidth from container + items-per-view, supporting fractional values (peek effect).
    // Same formula as spread-carousel (margin count = itemsPerView - 1, fractional) so widths match 1:1.
    const totalMarginSpace = this.marginRight * (this.itemsPerView - 1);
    const availableSpace = containerWidth - totalMarginSpace;
    this.itemWidth = Math.floor(availableSpace / this.itemsPerView);

    const visibleCount = Math.floor(this.itemsPerView);
    this.maxIndex = Math.max(0, this.streams.length - visibleCount);
    this.showNavigation = config.nav && this.streams.length > visibleCount;
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
    }
    this.updateTranslate();
    this.cdr.detectChanges();
  }

  prev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.isAnimating = true;
      this.updateTranslate();
    }
  }

  next(): void {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.isAnimating = true;
      this.updateTranslate();
    }
  }

  private updateTranslate(): void {
    this.translateX = -(this.currentIndex * (this.itemWidth + this.marginRight));
  }

  // --- Touch/drag support ---

  onTouchStart(event: TouchEvent): void {
    this.dragStartX = event.touches[0].clientX;
    this.dragStartTranslate = this.translateX;
    this.isDragging = false;
    this.isAnimating = false;
  }

  onTouchMove(event: TouchEvent): void {
    this.dragCurrentX = event.touches[0].clientX;
    const diff = this.dragCurrentX - this.dragStartX;
    if (Math.abs(diff) > 10) {
      this.isDragging = true;
      this.translateX = this.dragStartTranslate + diff;
    }
  }

  onTouchEnd(): void {
    if (!this.isDragging) return;
    const diff = this.dragCurrentX - this.dragStartX;
    const threshold = this.itemWidth / 3;
    if (diff < -threshold && this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    } else if (diff > threshold && this.currentIndex > 0) {
      this.currentIndex--;
    }
    this.isAnimating = true;
    this.updateTranslate();
    setTimeout(() => this.isDragging = false, 100);
  }
}
